/** Shared Chart.js fragments reused across the chart components, so the tooltip
 *  formatting, legend style, zoom behaviour and dark/light palette stay in one place. */

export function chartPalette(darkMode: boolean): { textColor: string; gridColor: string } {
  return {
    textColor: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    gridColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  };
}

/** Tooltip title: the hovered point's time as "HH:mm" (de-DE). */
export function timeTooltipTitle(items: any[]): string {
  const ts = items[0]?.parsed.x;
  return ts != null
    ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : '';
}

/** Legend label config that renders each dataset as a coloured line swatch. */
export function lineLegendLabels(textColor: string): any {
  return {
    color: textColor,
    usePointStyle: true,
    generateLabels: (chart: any) =>
      chart.data.datasets.map((ds: any, i: number) => ({
        text:        ds.label ?? '',
        fillStyle:   ds.borderColor,
        strokeStyle: ds.borderColor,
        lineDash:    ds.borderDash ?? [],
        lineWidth:   2,
        hidden:      !chart.isDatasetVisible(i),
        datasetIndex: i,
        pointStyle:  'line',
        fontColor:   textColor,
      })),
  };
}

/** The standard drag/pinch x-axis zoom plugin config. */
export function dragZoom(onComplete: () => void = () => {}): any {
  return {
    zoom: {
      drag: { enabled: true, backgroundColor: 'rgba(13,110,253,0.1)', borderColor: 'rgba(13,110,253,0.3)', borderWidth: 1 },
      pinch: { enabled: true },
      mode: 'x' as const,
      onZoomComplete: onComplete,
    },
  };
}

/**
 * x-axis tick formatter appropriate to the visible span: HH:00 within ~2 days,
 * DD.MM up to ~3 months, otherwise MM.YY. Used for the expanded charts whose
 * range can be Tag/Woche/Monat/Jahr.
 */
export function timeAxisTick(minMs: number, maxMs: number): (val: number | string) => string | null {
  const span = maxMs - minMs;
  const day = 24 * 3600 * 1000;
  if (span <= 2 * day) {
    return (val) => {
      const d = new Date(val as number);
      return d.getMinutes() === 0 ? `${String(d.getHours()).padStart(2, '0')}:00` : null;
    };
  }
  if (span <= 92 * day) {
    return (val) => {
      const d = new Date(val as number);
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
  }
  return (val) => {
    const d = new Date(val as number);
    return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
  };
}

/** x-axis tick label for a rolling 24h window: "Jetzt" / "–12h" / "–24h", else null. */
export function relativeDayTick(val: number | string): string | null {
  const diff = Date.now() - (val as number);
  const h24 = 24 * 3600 * 1000;
  const h12 = 12 * 3600 * 1000;
  if (diff < 5 * 60 * 1000)                   return 'Jetzt';
  if (Math.abs(diff - h12) < 30 * 60 * 1000)  return '–12h';
  if (Math.abs(diff - h24) < 30 * 60 * 1000)  return '–24h';
  return null;
}
